/* ME → WRITING — the screen's half (the prompt's half is api/_lib/writing.js).
 *
 * The option IDS are shared with the server: CRA cannot import from outside
 * src/, so there are two copies, and writingShape.test.js fails the moment
 * their ids disagree. The WORDS here are for her; the server has its own,
 * written for Brill.
 */

export const WRITING_ASK = [
  { id: "quiet", label: "Quiet", hint: "An invitation. No pressure — the reader arrives at the ask herself." },
  { id: "clear", label: "Clear", hint: "A direct ask, with the cost of waiting said plainly." },
  { id: "direct", label: "Direct response", hint: "Urgency, stacked value and strong calls to action — all of it true." },
];
/* Spec decision 1 (2026-09-23): Clear, when she has not chosen. Shown as the
   default on screen; NOT written until she presses one, so a coach who never
   opens this screen gets exactly the drafting she had before. */
export const DEFAULT_ASK = "clear";

export const WRITING_URGENCY = [
  { id: "yes", label: "Yes, when it's real", hint: "A real deadline, a real cap on places, the real cost of waiting." },
  { id: "no", label: "No", hint: "No deadlines or cost-of-waiting lines unless you ask for one." },
];

export const WRITING_HABITS = [
  { id: "dashes", label: "Em dashes (—)" },
  { id: "exclamations", label: "Exclamation points" },
  { id: "emoji", label: "Emoji" },
  { id: "questionOpeners", label: "Opening with a question" },
  { id: "lists", label: "Bullet lists" },
  { id: "threes", label: "Lists of three" },
];

/* ME → VOICE: one row per tool, in the nav's order (Build, Grow, Run). The ids
   are the tools' guide keys — the ones api/_lib/prompts.js TOOL_VOICES holds —
   and writingShape.test.js fails if one here is not a real voice, or if a
   voice exists that has no row and is not named as deliberately left out. */
export const VOICE_TOOLS = [
  { id: "offer_ladder", label: "Offer Ladder" },
  { id: "website_copy", label: "Sites" },
  { id: "presentation_copy", label: "Presentations" },
  { id: "course_copy", label: "Courses" },
  { id: "social_generator", label: "Social" },
  { id: "newsletter_copy", label: "Newsletter" },
  { id: "outreach_copy", label: "Outreach" },
  { id: "call_script_copy", label: "Call scripts" },
  { id: "video_script_copy", label: "Video scripts" },
];
/* `lesson_plans` is the older Course chat, which Courses' drafting screen
   replaced; it keeps its voice for anyone still in it, but a row for it would
   be two Courses rows. */
/* `content_studio` is Content Plan, off the nav since 2026-09-17 and replaced
   by Social's Calendar tab (her read, 2026-10-06: "just a calendar view of
   your posts") — a voice row for a tool she cannot open is clutter. */
export const VOICE_TOOLS_LEFT_OUT = ["lesson_plans", "content_studio"];

/* The tools that learn from her edits — they keep Brill's words beside hers.
   Mirrors api/_lib/voiceLearn.js LEARNS; writingShape.test.js holds them equal. */
export const LEARNING_TOOLS = ["social_generator"];

export const EMPTY_WRITING = { ask: null, urgency: null, habits: {}, words: "", rules: [], tools: {} };

const ruleId = () => "wr_" + Math.random().toString(36).slice(2, 10);

/** Whatever is stored, as the screen can use it. Never throws. */
export function readWriting(raw) {
  const w = raw && typeof raw === "object" ? raw : {};
  const habits = {};
  for (const h of WRITING_HABITS) {
    const v = w.habits?.[h.id];
    if (v === "use" || v === "avoid") habits[h.id] = v;
  }
  return {
    ask: WRITING_ASK.some((a) => a.id === w.ask) ? w.ask : null,
    urgency: WRITING_URGENCY.some((u) => u.id === w.urgency) ? w.urgency : null,
    habits,
    words: typeof w.words === "string" ? w.words : "",
    rules: (Array.isArray(w.rules) ? w.rules : [])
      .filter((r) => r && typeof r === "object")
      .map((r) => ({ id: r.id || ruleId(), text: String(r.text || ""), learned: !!r.learned, at: r.at || null })),
    tools: Object.fromEntries(Object.entries(w.tools && typeof w.tools === "object" ? w.tools : {})
      .filter(([, t]) => t && typeof t === "object")
      .map(([id, t]) => [id, {
        ask: WRITING_ASK.some((a) => a.id === t.ask) ? t.ask : null,
        notes: typeof t.notes === "string" ? t.notes : "",
        /* Learned from her edits (api/_lib/voiceLearn.js), and the bookkeeping
           that stops the same edits being paid for twice — only on a tool that
           has learned something, so the rest keep their two fields. */
        ...learnedPart(t),
      }])),
  };
}

function learnedPart(t) {
  const learned = (Array.isArray(t.learned) ? t.learned : []).filter((r) => r && typeof r === "object")
    .map((r) => ({ id: r.id || ruleId(), text: String(r.text || ""), at: r.at || null }));
  const seen = Array.isArray(t.seen) ? t.seen.filter((k) => typeof k === "string") : [];
  if (!learned.length && !seen.length && !t.lastLearnedAt) return {};
  return { learned, seen, lastLearnedAt: typeof t.lastLearnedAt === "string" ? t.lastLearnedAt : null, lastLearnedFrom: Number(t.lastLearnedFrom) || 0 };
}

/** One tool's settings, set or cleared. */
export function setTool(w, id, patch) {
  return { ...w, tools: { ...(w.tools || {}), [id]: { ask: null, notes: "", ...(w.tools?.[id] || {}), ...patch } } };
}

/** What a collapsed tool row says: its own settings, or that it follows yours. */
export function toolSummary(w, id) {
  const t = w.tools?.[id];
  const parts = [];
  if (t?.ask) parts.push(WRITING_ASK.find((a) => a.id === t.ask).label);
  if (String(t?.notes || "").trim()) parts.push("Notes");
  const n = (t?.learned || []).filter((r) => String(r.text || "").trim()).length;
  if (n) parts.push(`${n} learned`);
  return parts.length ? parts.join(" · ") : "Your default";
}

/** A new rule she is about to type. */
export const newRule = () => ({ id: ruleId(), text: "", learned: false, at: new Date().toISOString() });

/** What is stored: empty rules dropped, so a row she added and left blank does
    not ride into every prompt as a blank line. */
export function writingForSave(w) {
  /* A tool is kept while it holds anything — including what was learned there
     and which edits were learned from, or the next press would pay for them
     again. */
  const tools = Object.fromEntries(Object.entries(w.tools || {})
    .map(([id, t]) => [id, {
      ask: t?.ask || null,
      notes: String(t?.notes || "").trim(),
      ...learnedPart({ ...t, learned: (t?.learned || []).filter((r) => String(r.text || "").trim()).map((r) => ({ ...r, text: String(r.text).trim() })) }),
    }])
    .filter(([, t]) => t.ask || t.notes || (t.learned || []).length || (t.seen || []).length));
  return { ...w, rules: (w.rules || []).filter((r) => String(r.text || "").trim()), tools };
}

/** A habit button pressed: the same one again clears it back to "the format decides". */
export const toggleHabit = (habits, id, value) => {
  const next = { ...habits };
  if (next[id] === value) delete next[id];
  else next[id] = value;
  return next;
};
